import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useLocation, useNavigate } from "react-router-dom";
import { useUnsavedChanges } from "@/hooks";
import { UnsavedChangesDialog } from "@/components/common";
import {
  ChevronsUpDown,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Building2,
  CheckCheck,
  Landmark,
  GitBranch,
  Globe2,
  Info,
  FileText,
  Filter,
  Search,
  SearchX,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

import {
  AppDialog,
  MultiSelect,
  PageHeader,
  PickerField,
  SelectField,
} from "@/components/common";
import { Breadcrumbs } from "@/features/esg/components/reports/reportPreview/Breadcrumbs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { getUnitTypeMeta } from "@/features/organization/utils/organization.helpers";
import OrgStatusTag from "@/features/organization/components/OrgStatusTag";
import {
  buildOrgStatusInfoMap,
  buildOrgNameToStatusInfoMap,
} from "@/components/common/organization.utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { PATHS } from "@/lib/router";
import { showToast } from "@/lib/utils/toast";
import { useTran } from "@/hooks";
import { useOrganizationStore } from "@/features/organization/hooks/useOrganizationStore";
import { reportPreviewService } from "@/features/esg/services";
import { useAuthStore } from "@/features/auth";
import { filterOrgTreeByIds } from "@/features/data-entry/utils/orgTree.helpers";
import {
  isSuperAdminRole,
  normalizeOrgId,
} from "@/features/esg/utils/org-scope.utils";
import { boundariesOverlappingRange } from "@/features/esg/utils/report-preview-boundary-interval.utils";
import { formatReportingYearPeriod } from "@/features/esg/utils/period-constants";
import { Can } from "@/hooks/useCan/Can";

import CreateReportModal from "@/features/esg/components/reports/reportPreview/CreateReportModal";
import {
  ReportPreviewProvider,
  useReportPreviewContext,
} from "@/features/esg/hooks/reportPreview.context";
import { REPORT_PREVIEW_OPTIONS } from "@/features/esg/hooks/reportPreview.options";

const INDENT_SIZE = 18;

const getNodeKey = (node) => node?.id || node?.name;
const getLevelNumber = (value) => {
  if (!value?.startsWith?.("level_")) return null;
  const level = Number.parseInt(value.split("_")[1], 10);
  return Number.isFinite(level) ? level : null;
};

const ICON_STYLES = {
  parent: { icon: Building2, color: "text-indigo-600" },
  building: { icon: Building2, color: "text-sky-600" },
  landmark: { icon: Landmark, color: "text-emerald-600" },
  git: { icon: GitBranch, color: "text-amber-600" },
  globe: { icon: Globe2, color: "text-emerald-600" },
};

const getPreviewUnitMeta = (unitLevel, filterType) => {
  if (unitLevel === "parent_company") return ICON_STYLES.parent;

  if (filterType === "parent_company") {
    if (unitLevel === "level_1") return ICON_STYLES.building;
    if (unitLevel === "level_2") return ICON_STYLES.landmark;
    if (unitLevel === "level_3") return ICON_STYLES.git;
  }

  if (filterType === "level_1") {
    if (unitLevel === "level_1") return ICON_STYLES.building;
  }

  if (filterType === "level_2") {
    if (unitLevel === "level_1") return ICON_STYLES.building;
    if (unitLevel === "level_2") return ICON_STYLES.landmark;
  }

  if (filterType === "country") {
    if (unitLevel === "country") return ICON_STYLES.globe;
    if (unitLevel === "level_1") return ICON_STYLES.building;
    if (unitLevel === "level_2") return ICON_STYLES.landmark;
  }

  if (filterType === "level_3") {
    if (unitLevel === "level_1") return ICON_STYLES.building;
    if (unitLevel === "level_2") return ICON_STYLES.landmark;
    if (unitLevel === "level_3") return ICON_STYLES.git;
  }

  return getUnitTypeMeta(unitLevel);
};

const toSearchText = (node = {}) =>
  [node.name, node.code, node.country, node.countryLabel]
    .filter((value) => typeof value === "string" && value.trim())
    .join(" ")
    .toLowerCase();

const filterTreeByQuery = (nodes = [], query = "") => {
  if (!query) return nodes;
  const normalized = query.trim().toLowerCase();

  const filterNode = (node) => {
    const children = Array.isArray(node?.children) ? node.children : [];
    const filteredChildren = children
      .map((child) => filterNode(child))
      .filter(Boolean);
    const matches = toSearchText(node).includes(normalized);

    if (matches || filteredChildren.length > 0) {
      return { ...node, children: filteredChildren };
    }
    return null;
  };

  return nodes.map((node) => filterNode(node)).filter(Boolean);
};

const parseYearMonthValue = (value) => {
  if (!value || typeof value !== "string") return null;
  const [yearRaw, monthRaw] = value.split("-");
  const year = Number.parseInt(yearRaw, 10);
  const month = Number.parseInt(monthRaw, 10);

  if (!Number.isFinite(year) || !Number.isFinite(month)) return null;
  if (month < 1 || month > 12) return null;

  return { year, month };
};

const toYearMonthValue = (value) => {
  if (!value?.year || !value?.month) return "";
  return `${String(value.year).padStart(4, "0")}-${String(value.month).padStart(2, "0")}`;
};

const FilterSelectField = ({
  label,
  value,
  onChange,
  options,
  placeholder,
  required,
  error,
}) => (
  <div className="space-y-2">
    <Label className="text-sm">
      {label}
      {required ? <span className="text-destructive ml-1">*</span> : null}
    </Label>
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger className={cn("w-full", error && "border-destructive")}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
    {error ? <p className="text-sm text-destructive">{error}</p> : null}
  </div>
);

const getAllSelectableChildIds = (node, selectableLevels) => {
  const ids = [];
  const walk = (n) => {
    const id = n?.id ? String(n.id) : null;
    if (id && selectableLevels.has(n?.unitLevel)) ids.push(id);
    if (Array.isArray(n?.children)) n.children.forEach(walk);
  };
  if (Array.isArray(node?.children)) node.children.forEach(walk);
  return ids;
};

const NameTreeNode = ({
  node,
  depth,
  selectedSet,
  collapsedNodes,
  onToggleCollapse,
  onToggleSelect,
  onBatchToggle,
  selectableLevels,
  filterType,
  statusMap,
}) => {
  const { t } = useTran();
  const nodeKey = getNodeKey(node);
  const nodeValue = node?.id ?? nodeKey;
  const normalizedValue = nodeValue ? String(nodeValue) : "";
  const label = node?.name || normalizedValue;
  const hasChildren = Array.isArray(node?.children) && node.children.length > 0;
  const isCollapsed = nodeKey ? collapsedNodes.has(nodeKey) : false;
  const isSelectable = selectableLevels.has(node?.unitLevel);
  const isChecked = normalizedValue ? selectedSet.has(normalizedValue) : false;
  const meta = getPreviewUnitMeta(node?.unitLevel, filterType);
  const Icon = meta.icon;
  // statusMap is Map<id, { status, inactiveDate }> — see buildOrgStatusInfoMap.
  const orgStatusInfo = statusMap?.get(normalizedValue);
  const orgStatus = orgStatusInfo?.status;
  const orgInactiveDate = orgStatusInfo?.inactiveDate;

  const selectableChildIds = hasChildren
    ? getAllSelectableChildIds(node, selectableLevels)
    : [];
  const hasSelectableChildren = selectableChildIds.length > 0;
  const areAllChildrenSelected =
    hasSelectableChildren &&
    selectableChildIds.every((id) => selectedSet.has(id));

  return (
    <div className="space-y-1">
      <div
        className="flex items-center gap-2 rounded-radius px-2 py-2 hover:bg-muted/60"
        style={{ paddingLeft: `${8 + depth * INDENT_SIZE}px` }}
      >
        {hasChildren ? (
          <button
            type="button"
            aria-label={isCollapsed ? "Expand" : "Collapse"}
            onClick={() => nodeKey && onToggleCollapse(nodeKey)}
            className="flex h-5 w-5 items-center justify-center rounded border border-border bg-background text-muted-foreground transition hover:bg-muted"
          >
            {isCollapsed ? (
              <ChevronRight className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            )}
          </button>
        ) : (
          <span className="h-5 w-5" />
        )}

        {isSelectable ? (
          <Checkbox
            checked={isChecked}
            onCheckedChange={(checked) =>
              normalizedValue &&
              onToggleSelect(normalizedValue, Boolean(checked))
            }
          />
        ) : (
          <span className="h-4 w-4" />
        )}

        <span
          className={`flex h-7 w-7 items-center justify-center rounded-full bg-background shadow-sm ${meta.color}`}
        >
          <Icon className="h-3.5 w-3.5" />
        </span>

        <div className="flex flex-1 flex-col gap-0.5 text-left text-sm text-foreground min-w-0">
          <span className="truncate">{label}</span>
          {orgStatus && (
            <OrgStatusTag
              status={orgStatus}
              inactiveDate={orgInactiveDate}
              className="text-[10px] shrink-0"
            />
          )}
        </div>

        {hasSelectableChildren && onBatchToggle ? (
          <Button
            variant="ghost"
            size="xs"
            className={
              areAllChildrenSelected
                ? "bg-orange-100 text-orange-700 hover:bg-orange-200"
                : "bg-blue-100 text-blue-700 hover:bg-blue-200"
            }
            onClick={(e) => {
              e.stopPropagation();
              onBatchToggle(selectableChildIds, !areAllChildrenSelected);
            }}
          >
            {areAllChildrenSelected ? t("Unselect All") : t("Select All")}
          </Button>
        ) : null}
      </div>

      {hasChildren && !isCollapsed
        ? node.children.map((child) => (
            <NameTreeNode
              key={getNodeKey(child) || child.name}
              node={child}
              depth={depth + 1}
              selectedSet={selectedSet}
              collapsedNodes={collapsedNodes}
              onToggleCollapse={onToggleCollapse}
              onToggleSelect={onToggleSelect}
              onBatchToggle={onBatchToggle}
              selectableLevels={selectableLevels}
              filterType={filterType}
              statusMap={statusMap}
            />
          ))
        : null}
    </div>
  );
};

const PreviewReportPageContent = observer(() => {
  const { t } = useTran();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const permissions = user?.permissions || [];
  const isAdmin = isSuperAdminRole(user?.role);
  const {
    selectedFilters,
    appliedFilters,
    previewData,
    previewLoading,
    previewError,
    setFilter,
    applyFilters,
    resetFilters,
    openCreateModal,
    loadSavedReport,
    saveDraftChanges,
    completeReport,
  } = useReportPreviewContext();
  const {
    tree: orgTree,
    loading: orgLoading,
    fetchHierarchy,
  } = useOrganizationStore();

  const [showValidation, setShowValidation] = useState(false);
  const [boundaryData, setBoundaryData] = useState(null);
  const [loadedSavedReportId, setLoadedSavedReportId] = useState("");
  const [editingDraftReport, setEditingDraftReport] = useState(null);
  const [isCompleteConfirmOpen, setIsCompleteConfirmOpen] = useState(false);
  // True while a saved draft's filters are being hydrated (both the
  // synchronous prefill and the async fetchReport completion that follows) —
  // suppresses the buSide/date reset effects below so they don't clobber the
  // hydrated companies/buSide the moment loadSavedReport sets them. Cleared
  // only when the user genuinely interacts with Filter/Reporting Year,
  // not on a timer, so it protects both hydration commits.
  const isHydratingRef = useRef(false);
  const loadedFiltersRef = useRef(null);

  const isDirty = (() => {
    const hasFilters =
      !!appliedFilters ||
      !!selectedFilters.startDate ||
      selectedFilters.companies.length > 0;
    if (!hasFilters) return false;
    if (loadedFiltersRef.current === "pending") return false;
    if (loadedFiltersRef.current) {
      return JSON.stringify(appliedFilters) !== loadedFiltersRef.current;
    }
    return true;
  })();
  const {
    showDialog: showUnsavedDialog,
    confirmDiscard,
    cancelDiscard,
    bypass,
  } = useUnsavedChanges(isDirty);
  useEffect(() => {
    if (!orgTree?.length && !orgLoading) {
      fetchHierarchy?.().catch(() => {});
    }
  }, [fetchHierarchy, orgLoading, orgTree?.length]);

  useEffect(() => {
    let isMounted = true;

    const loadBoundaryData = async () => {
      try {
        const data = await reportPreviewService.getFilterOptions(
          {},
          { throwOnError: false },
        );
        if (isMounted) {
          setBoundaryData(data ?? null);
        }
      } catch {
        if (isMounted) {
          setBoundaryData(null);
        }
      }
    };

    void loadBoundaryData();
    return () => {
      isMounted = false;
    };
  }, []);

  // "Relevant boundaries" = those overlapping the currently-selected date range, per the
  // boundary-driven filter spec. Before a date is picked, this falls back to every boundary
  // (see boundariesOverlappingRange), matching the initial full-span picker view.
  const selectedDateRange = useMemo(() => {
    const start = parseYearMonthValue(selectedFilters.startDate);
    const end = parseYearMonthValue(selectedFilters.endDate);
    if (!start || !end) return null;
    return {
      startYear: start.year,
      startMonth: start.month,
      endYear: end.year,
      endMonth: end.month,
    };
  }, [selectedFilters.startDate, selectedFilters.endDate]);

  const relevantBoundaries = useMemo(
    () =>
      boundariesOverlappingRange(boundaryData?.boundaries, selectedDateRange),
    [boundaryData?.boundaries, selectedDateRange],
  );

  const showParentOption = useMemo(
    () => relevantBoundaries.some((boundary) => boundary.isCompanySelected),
    [relevantBoundaries],
  );

  const boundaryFrameworkOptions = useMemo(
    () => [
      ...new Set(
        relevantBoundaries.flatMap((boundary) => boundary.frameworkCodes || []),
      ),
    ],
    [relevantBoundaries],
  );

  const boundaryAllOrganization = useMemo(
    () => relevantBoundaries.some((boundary) => boundary.isAllOrganization),
    [relevantBoundaries],
  );

  const parentOrgNodeId = useMemo(
    () =>
      (orgTree || []).find((node) => node?.unitLevel === "parent_company")?.id,
    [orgTree],
  );

  // null = no boundary-based restriction (isAllOrganization permits everything).
  const boundaryOrgIdSet = useMemo(() => {
    if (boundaryAllOrganization) return null;
    const ids = new Set(
      relevantBoundaries
        .flatMap((boundary) => boundary.organizationIds || [])
        .map(normalizeOrgId)
        .filter(Boolean),
    );
    if (showParentOption && parentOrgNodeId) {
      ids.add(normalizeOrgId(parentOrgNodeId));
    }
    return ids;
  }, [
    relevantBoundaries,
    boundaryAllOrganization,
    showParentOption,
    parentOrgNodeId,
  ]);

  // Reporting Year dropdown options — one per ReportingBoundary doc, keyed by
  // its own id (not bare year) so two boundaries that happen to share a
  // startYear (different frameworks/orgs) still show as distinct, unambiguous
  // options instead of colliding.
  const reportingYearOptions = useMemo(
    () =>
      (boundaryData?.boundaries || [])
        .filter((boundary) => boundary?.id)
        .map((boundary) => ({
          id: boundary.id,
          label: formatReportingYearPeriod(boundary),
        })),
    [boundaryData?.boundaries],
  );

  // Reverse-maps the currently selected startDate/endDate back to whichever
  // boundary produced them, so the dropdown shows the right option selected.
  // Resolves to "" or the actual value depending on whether a saved draft's
  // startDate/endDate exactly matches a boundary — a draft saved under the
  // old free-form date range (a custom, non-boundary-aligned range) simply
  // won't match anything here, and is left as-is rather than forced to
  // change (its stored dates keep working normally; the dropdown just shows
  // no selection for it).
  const selectedReportingBoundaryId = useMemo(() => {
    const match = (boundaryData?.boundaries || []).find((boundary) => {
      const boundaryStart = toYearMonthValue({
        year: Number(boundary.startYear),
        month: Number(boundary.startMonth),
      });
      const boundaryEnd = toYearMonthValue({
        year: Number(boundary.endYear),
        month: Number(boundary.endMonth),
      });
      return (
        boundaryStart === selectedFilters.startDate &&
        boundaryEnd === selectedFilters.endDate
      );
    });
    return match?.id || "";
  }, [
    boundaryData?.boundaries,
    selectedFilters.startDate,
    selectedFilters.endDate,
  ]);

  const filteredOrgTree = useMemo(() => {
    if (!orgTree?.length) return [];

    let allowedSet = null;
    if (!isAdmin) {
      const orgIds = Array.isArray(user?.organizationUnitId)
        ? user.organizationUnitId
        : user?.organizationUnitId
          ? [user.organizationUnitId]
          : [];
      allowedSet = new Set(orgIds.map(normalizeOrgId).filter(Boolean));
    }

    if (!boundaryAllOrganization && boundaryOrgIdSet) {
      allowedSet = allowedSet
        ? new Set([...allowedSet].filter((id) => boundaryOrgIdSet.has(id)))
        : new Set(boundaryOrgIdSet);
    }

    if (!allowedSet) return orgTree;
    if (!allowedSet.size) return [];
    return filterOrgTreeByIds(orgTree, allowedSet);
  }, [
    isAdmin,
    orgTree,
    user?.organizationUnitId,
    boundaryAllOrganization,
    boundaryOrgIdSet,
  ]);

  useEffect(() => {
    const savedReport = location.state?.previewReport;
    const savedReportId = savedReport?.id || savedReport?._id || "";
    const isDraftEdit =
      location.state?.editMode === "draft" &&
      String(savedReport?.status || "draft").toLowerCase() !== "completed";

    setEditingDraftReport(isDraftEdit ? savedReport : null);

    if (!savedReportId || savedReportId === loadedSavedReportId) return;

    isHydratingRef.current = true;
    loadedFiltersRef.current = "pending";
    loadSavedReport?.(savedReport).catch(() => {});
    setLoadedSavedReportId(savedReportId);
  }, [loadSavedReport, loadedSavedReportId, location.state]);

  useEffect(() => {
    if (loadedFiltersRef.current === "pending" && appliedFilters) {
      loadedFiltersRef.current = JSON.stringify(appliedFilters);
    }
  }, [appliedFilters]);

  const resolveCountryLabel = useCallback((value) => {
    if (!value) return "";
    if (String(value).startsWith("country:")) {
      return String(value).replace(/^country:/, "");
    }
    return String(value);
  }, []);

  const buildCountryId = useCallback(
    (label) => `country:${resolveCountryLabel(label)}`,
    [resolveCountryLabel],
  );

  const orgNodeById = useMemo(() => {
    const map = new Map();
    const walk = (node) => {
      const nodeId = node?.id ?? node?.name;
      if (nodeId) {
        map.set(String(nodeId), node);
      }
      (node?.children || []).forEach((child) => walk(child));
    };
    (orgTree || []).forEach((node) => walk(node));
    return map;
  }, [orgTree]);

  const orgStatusMap = useMemo(
    () => buildOrgStatusInfoMap(orgTree || []),
    [orgTree],
  );

  const orgNameToStatusMap = useMemo(
    () => buildOrgNameToStatusInfoMap(orgTree || []),
    [orgTree],
  );

  const resolveNodeLabel = useCallback(
    (value) => {
      if (!value) return "";
      if (String(value).startsWith("country:")) {
        return resolveCountryLabel(value);
      }
      const node = orgNodeById.get(String(value));
      return node?.name || String(value);
    },
    [orgNodeById, resolveCountryLabel],
  );

  const orgEntityMaps = useMemo(() => {
    const companyToSites = new Map();
    const buToCompanies = new Map();
    const companyToBu = new Map();
    const countryToCompanies = new Map();
    const siteToCompany = new Map();

    const pushToMap = (map, key, value) => {
      if (!key || !value) return;
      const list = map.get(key) || [];
      if (!list.includes(value)) list.push(value);
      map.set(key, list);
    };

    const resolveCountry = (node) =>
      node?.locationLabel ||
      node?.countryLabel ||
      node?.country ||
      node?.regionLabel;

    const walk = (node, currentBu, currentCompany) => {
      const nodeId = node?.id ?? node?.name;
      if (!nodeId) return;
      const normalizedId = String(nodeId);
      const nextBu = node?.unitLevel === "level_1" ? normalizedId : currentBu;
      const nextCompany =
        node?.unitLevel === "level_2" ? normalizedId : currentCompany;

      if (node?.unitLevel === "level_2") {
        if (!companyToSites.has(normalizedId)) {
          companyToSites.set(normalizedId, []);
        }
        if (nextBu) pushToMap(buToCompanies, nextBu, normalizedId);
        if (nextBu) companyToBu.set(normalizedId, nextBu);
        const countryLabel = resolveCountry(node);
        if (countryLabel) {
          pushToMap(
            countryToCompanies,
            resolveCountryLabel(countryLabel),
            normalizedId,
          );
        }
      }

      if (node?.unitLevel === "level_3" && nextCompany) {
        pushToMap(companyToSites, nextCompany, normalizedId);
        siteToCompany.set(normalizedId, nextCompany);
      }

      (node?.children || []).forEach((child) =>
        walk(child, nextBu, nextCompany),
      );
    };

    (orgTree || []).forEach((node) => walk(node, null, null));

    return {
      companyToSites,
      buToCompanies,
      companyToBu,
      countryToCompanies,
      siteToCompany,
    };
  }, [orgTree, resolveCountryLabel]);

  const [nameDropdownOpen, setNameDropdownOpen] = useState(false);
  const [nameQuery, setNameQuery] = useState("");
  const [collapsedNodes, setCollapsedNodes] = useState(() => new Set());
  const [relationshipCollapsedNodes, setRelationshipCollapsedNodes] = useState(
    () => new Set(),
  );
  const allowIncludeAllEntities =
    selectedFilters.buSide === "parent_company" ||
    selectedFilters.buSide === "level_1" ||
    selectedFilters.buSide === "level_2";

  const selectedNameSet = useMemo(
    () => new Set(selectedFilters.companies),
    [selectedFilters.companies],
  );

  const selectedNameLabels = useMemo(
    () =>
      selectedFilters.companies
        .map((value) => resolveNodeLabel(value))
        .filter(Boolean),
    [resolveNodeLabel, selectedFilters.companies],
  );

  const buildTreeForFilter = useCallback(
    (filterType) => {
      if (!filterType) return [];
      if (filterType === "parent_company") {
        return (filteredOrgTree || [])
          .filter(
            (node) => node?.unitLevel === "parent_company" && !node?.isGroup,
          )
          .map((node) => ({
            ...node,
            children: [],
          }));
      }

      const maxLevel =
        filterType === "level_1"
          ? 1
          : filterType === "level_2"
            ? 2
            : filterType === "level_3"
              ? 3
              : null;

      if (filterType === "country") {
        const countries = new Map();
        const walk = (nodes = []) => {
          nodes.forEach((node) => {
            if (node?.isGroup) {
              if (Array.isArray(node.children)) walk(node.children);
              return;
            }
            const label =
              node?.locationLabel ||
              node?.countryLabel ||
              node?.country ||
              node?.regionLabel;
            if (label) {
              const normalized = resolveCountryLabel(label);
              countries.set(normalized, normalized);
            }
            if (Array.isArray(node.children)) walk(node.children);
          });
        };
        walk(filteredOrgTree);
        return Array.from(countries.values()).map((label) => ({
          id: buildCountryId(label),
          name: label,
          unitLevel: "country",
        }));
      }

      const trimTree = (nodes = []) =>
        nodes
          .map((node) => {
            const level = getLevelNumber(node.unitLevel);
            if (level && maxLevel && level > maxLevel) return null;
            if (node.isGroup && level === maxLevel) {
              // A boundary-excluded ancestor AT the level currently being selected has no
              // reason to show (you're picking orgs at this exact level, and it isn't one
              // you're allowed to pick) — unwrap it and promote its children. An ancestor
              // ABOVE the target level is left alone below so it keeps showing as
              // non-selectable context (same idea as "By Country" showing BU context even
              // though BUs aren't selectable there).
              return trimTree(node.children || []);
            }
            const children = trimTree(node.children || []);
            if (node.unitLevel === "parent_company") {
              return children;
            }
            return { ...node, children };
          })
          .flat()
          .filter(Boolean);

      return trimTree(filteredOrgTree);
    },
    [buildCountryId, filteredOrgTree, resolveCountryLabel],
  );

  const treeForFilter = useMemo(
    () => buildTreeForFilter(selectedFilters.buSide),
    [buildTreeForFilter, selectedFilters.buSide],
  );

  const visibleTree = useMemo(
    () => filterTreeByQuery(treeForFilter, nameQuery),
    [treeForFilter, nameQuery],
  );

  const selectableLevels = useMemo(() => {
    if (selectedFilters.buSide === "parent_company") {
      return new Set(["parent_company"]);
    }
    if (selectedFilters.buSide === "level_1") {
      return new Set(["level_1"]);
    }
    if (selectedFilters.buSide === "level_2") {
      return new Set(["level_2"]);
    }
    if (selectedFilters.buSide === "level_3") {
      return new Set(["level_3"]);
    }
    if (selectedFilters.buSide === "country") {
      return new Set(["country"]);
    }
    return new Set();
  }, [selectedFilters.buSide]);

  const descendantNodesById = useMemo(() => {
    const map = new Map();

    const walk = (node) => {
      const idKey = node?.id ?? node?.name;
      const normalizedId = idKey ? String(idKey) : "";
      const descendantNodes = [];

      (node?.children || []).forEach((child) => {
        descendantNodes.push(...walk(child));
      });

      if (normalizedId) {
        map.set(normalizedId, [
          {
            id: normalizedId,
            name: node?.name || normalizedId,
            unitLevel: node.unitLevel,
          },
          ...descendantNodes,
        ]);
      }

      return normalizedId
        ? [
            {
              id: normalizedId,
              name: node?.name || normalizedId,
              unitLevel: node.unitLevel,
            },
            ...descendantNodes,
          ]
        : descendantNodes;
    };

    (treeForFilter || []).forEach((node) => walk(node));
    return map;
  }, [treeForFilter]);

  // Every selectable node across the whole tree for the current Filter (not
  // just one branch) — used to auto-select Name when there's only one choice.
  const allSelectableNodeIds = useMemo(() => {
    const ids = [];
    (treeForFilter || []).forEach((node) => {
      const idKey = node?.id ?? node?.name;
      const normalizedId = idKey ? String(idKey) : "";
      const nodes = normalizedId
        ? descendantNodesById.get(normalizedId) || []
        : [];
      nodes.forEach((item) => {
        if (selectableLevels.has(item.unitLevel)) ids.push(item.id);
      });
    });
    return ids;
  }, [treeForFilter, descendantNodesById, selectableLevels]);

  const handleToggleCollapse = useCallback((nodeKey) => {
    if (!nodeKey) return;
    setCollapsedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeKey)) next.delete(nodeKey);
      else next.add(nodeKey);
      return next;
    });
  }, []);

  const handleToggleName = useCallback(
    (nodeId, checked) => {
      if (
        !selectableLevels.has(descendantNodesById.get(nodeId)?.[0]?.unitLevel)
      ) {
        return;
      }

      const descendants = descendantNodesById.get(nodeId) || [];
      const next = new Set(selectedFilters.companies);

      descendants.forEach((item) => {
        if (!selectableLevels.has(item.unitLevel)) return;
        if (checked) next.add(item.id);
        else next.delete(item.id);
      });

      setFilter("companies", Array.from(next));
    },
    [
      descendantNodesById,
      selectedFilters.companies,
      selectableLevels,
      setFilter,
    ],
  );

  const handleBatchToggle = useCallback(
    (ids, shouldSelect) => {
      const next = new Set(selectedFilters.companies);
      ids.forEach((id) => {
        if (shouldSelect) next.add(id);
        else next.delete(id);
      });
      setFilter("companies", Array.from(next));
    },
    [selectedFilters.companies, setFilter],
  );

  const handleToggleRelationshipCollapse = useCallback((nodeKey) => {
    if (!nodeKey) return;
    setRelationshipCollapsedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeKey)) next.delete(nodeKey);
      else next.add(nodeKey);
      return next;
    });
  }, []);

  useEffect(() => {
    // When opening a saved draft for editing, skip the initial reset so the
    // hydrated filters from loadSavedReport survive. On a clean navigation
    // (no saved report in location state) reset as usual.
    const savedReportId =
      location.state?.previewReport?.id || location.state?.previewReport?._id;
    if (!savedReportId) {
      isHydratingRef.current = false;
      loadedFiltersRef.current = null;
      resetFilters();
    }
  }, [resetFilters, location.state]);

  useEffect(() => {
    // Skip while a saved draft's filters are still being hydrated — this
    // would otherwise wipe the companies loadSavedReport just set, both on
    // its initial sync prefill and on its async fetchReport completion.
    if (isHydratingRef.current) return;
    setFilter("companies", []);
  }, [selectedFilters.buSide, setFilter]);

  useEffect(() => {
    // The permitted buSide/org/framework set is derived from whichever reporting
    // boundaries overlap the selected date range (see relevantBoundaries above) — a
    // previously selected Report By type, organization, or framework can become invalid
    // the moment the date range changes (e.g. "By Parent" disappearing when
    // showParentOption flips false), so clear them rather than silently keep a now-stale
    // selection.
    if (isHydratingRef.current) return;
    setFilter("buSide", "");
    setFilter("companies", []);
    setFilter("frameworks", []);
  }, [selectedFilters.startDate, selectedFilters.endDate, setFilter]);

  // Auto-select Name when the current Filter leaves only one organization to
  // choose from — it's a required field anyway, so there's no "leave blank"
  // semantic being overridden. Guarded on an empty selection so it only fills
  // in the initial pick and never fights a user who deliberately clears it.
  // Declared after the reset effects above so it always runs last within the
  // same commit — otherwise the buSide/date reset effects (which also fire
  // when buSide just changed) would immediately clobber this auto-selection.
  useEffect(() => {
    if (
      allSelectableNodeIds.length === 1 &&
      selectedFilters.companies.length === 0
    ) {
      setFilter("companies", allSelectableNodeIds);
    }
  }, [allSelectableNodeIds, selectedFilters.companies.length, setFilter]);

  useEffect(() => {
    if (!allowIncludeAllEntities && selectedFilters.includeAllEntities) {
      setFilter("includeAllEntities", false);
    }
  }, [allowIncludeAllEntities, selectedFilters.includeAllEntities, setFilter]);

  const requiredFilterKeys = ["companies", "startDate", "endDate"];
  const requiredMissing = requiredFilterKeys.some((key) =>
    Array.isArray(selectedFilters[key])
      ? selectedFilters[key].length === 0
      : !selectedFilters[key],
  );
  // Filter/Name/Frameworks options are all derived from whichever boundary the
  // selected Reporting Year resolved to (see relevantBoundaries above), so they
  // only make sense once a year is picked — gate them instead of letting a
  // selection get silently wiped by the reactive-clear effect below. Picking a
  // Reporting Year always sets a matching, valid startDate/endDate pair (it's
  // sourced directly from a real boundary), so there's no invalid-range case
  // to guard against here anymore.
  const datesSelected =
    Boolean(selectedFilters.startDate) && Boolean(selectedFilters.endDate);
  const appliedMissing = appliedFilters
    ? requiredFilterKeys.some((key) =>
        Array.isArray(appliedFilters[key])
          ? appliedFilters[key].length === 0
          : !appliedFilters[key],
      )
    : true;

  const handleApply = async () => {
    setShowValidation(true);
    if (requiredMissing) return;
    await applyFilters();
  };

  const handleReset = () => {
    resetFilters();
    setShowValidation(false);
  };

  const appliedCountryLabels = useMemo(() => {
    if (appliedFilters?.buSide !== "country") return [];
    return (appliedFilters.companies || [])
      .map((value) => resolveCountryLabel(value))
      .filter(Boolean);
  }, [appliedFilters, resolveCountryLabel]);

  const canCreate = Boolean(appliedFilters) && !appliedMissing;
  const isEditingDraft = Boolean(editingDraftReport?.id);

  // Boundary-configured frameworks (date-range scoped, see relevantBoundaries above),
  // narrowed to ones with at least one disclosure actually assigned to the org (same
  // narrowing Data Entry's framework filter applies via buildAssignedFrameworks).
  const assignedFrameworkNameByCode = useMemo(
    () =>
      new Map(
        Array.isArray(boundaryData?.assignedFrameworks)
          ? boundaryData.assignedFrameworks.map((fw) => [fw.code, fw.name])
          : [],
      ),
    [boundaryData?.assignedFrameworks],
  );
  const frameworkOptions = useMemo(
    () =>
      boundaryFrameworkOptions.filter((code) =>
        assignedFrameworkNameByCode.has(code),
      ),
    [boundaryFrameworkOptions, assignedFrameworkNameByCode],
  );

  // Auto-select Frameworks when nothing is chosen yet: prefer JJ-Group
  // whenever it's assigned, regardless of how many other frameworks are also
  // available; otherwise fall back to auto-selecting the sole option when
  // there's only one (an empty selection is already treated as "all
  // frameworks" everywhere else in this file, and with a single option "all"
  // and "that one" are functionally identical).
  useEffect(() => {
    if (selectedFilters.frameworks.length !== 0) return;
    if (frameworkOptions.includes("JJ-Group")) {
      setFilter("frameworks", ["JJ-Group"]);
    } else if (frameworkOptions.length === 1) {
      setFilter("frameworks", [frameworkOptions[0]]);
    }
  }, [frameworkOptions, selectedFilters.frameworks.length, setFilter]);

  const handleMarkAsCompleted = useCallback(async () => {
    if (!editingDraftReport?.id) return;
    setIsCompleteConfirmOpen(false);
    try {
      await saveDraftChanges({
        reportId: editingDraftReport.id,
        name: editingDraftReport.name,
        description: editingDraftReport.description || "",
        presentedTo: editingDraftReport.presentedTo || "",
        includeCoverPage: Boolean(editingDraftReport.includeCoverPage ?? true),
        includeContentPage: Boolean(
          editingDraftReport.includeContentPage ?? true,
        ),
      });
      const completed = await completeReport(editingDraftReport.id);
      if (!completed) throw new Error();
      showToast.success(
        t('"{{name}}" marked as completed', {
          name: editingDraftReport.name,
        }),
      );
      bypass();
      navigate(PATHS.ESG_REPORT_PREVIEW_LIST, {
        state: { reportStatus: "completed" },
      });
    } catch {
      showToast.error(t("Unable to mark report as completed."));
    }
  }, [
    bypass,
    completeReport,
    editingDraftReport,
    navigate,
    saveDraftChanges,
    t,
  ]);

  const relationshipGroups = useMemo(() => {
    if (!appliedFilters) return [];
    const selected = Array.isArray(appliedFilters.companies)
      ? appliedFilters.companies
      : [];
    const includeAll = Boolean(appliedFilters.includeAllEntities);
    const {
      companyToSites,
      buToCompanies,
      companyToBu,
      countryToCompanies,
      siteToCompany,
    } = orgEntityMaps;

    if (!selected.length) return [];

    if (appliedFilters.buSide === "parent_company") {
      const buildGroupNode = (node, includeChildren = true) => ({
        title: node?.name || "",
        level: node?.unitLevel || "parent_company",
        items: [],
        children:
          includeChildren && Array.isArray(node?.children)
            ? node.children.map((child) => buildGroupNode(child, true))
            : [],
      });

      return selected
        .map((parentId) => orgNodeById.get(String(parentId)))
        .filter(Boolean)
        .map((node) => buildGroupNode(node, includeAll));
    }

    if (appliedFilters.buSide === "level_1") {
      return selected.map((buId) => ({
        title: resolveNodeLabel(buId),
        level: "level_1",
        items: includeAll
          ? (buToCompanies.get(String(buId)) || []).map((companyId) =>
              resolveNodeLabel(companyId),
            )
          : [],
        itemLevel: "level_2",
        children: includeAll
          ? (buToCompanies.get(String(buId)) || []).map((companyId) => ({
              title: resolveNodeLabel(companyId),
              level: "level_2",
              items: (companyToSites.get(String(companyId)) || []).map((site) =>
                resolveNodeLabel(site),
              ),
              itemLevel: "level_3",
            }))
          : [],
      }));
    }

    if (appliedFilters.buSide === "level_2") {
      const grouped = new Map();
      selected.forEach((companyId) => {
        const buId = companyToBu.get(String(companyId));
        const key = buId || t("Other");
        const list = grouped.get(key) || [];
        list.push(companyId);
        grouped.set(key, list);
      });

      return Array.from(grouped.entries()).map(([buId, companies]) => ({
        title: resolveNodeLabel(buId),
        level: "level_1",
        items: companies.map((companyId) => resolveNodeLabel(companyId)),
        itemLevel: "level_2",
        children: includeAll
          ? companies.map((companyId) => ({
              title: resolveNodeLabel(companyId),
              level: "level_2",
              items: (companyToSites.get(String(companyId)) || []).map((site) =>
                resolveNodeLabel(site),
              ),
              itemLevel: "level_3",
            }))
          : [],
      }));
    }

    if (appliedFilters.buSide === "level_3") {
      const grouped = new Map();
      selected.forEach((siteId) => {
        const companyId = siteToCompany.get(String(siteId));
        if (!companyId) return;
        const list = grouped.get(companyId) || [];
        list.push(siteId);
        grouped.set(companyId, list);
      });

      return Array.from(grouped.entries()).map(([companyId, sites]) => ({
        title: resolveNodeLabel(companyId),
        level: "level_2",
        items: sites.map((site) => resolveNodeLabel(site)),
        itemLevel: "level_3",
      }));
    }

    if (appliedFilters.buSide === "country") {
      return selected.map((countryValue) => {
        const countryLabel = resolveCountryLabel(countryValue);
        const companies = countryToCompanies.get(countryLabel) || [];
        return {
          title: countryLabel,
          level: "country",
          items: companies.map((companyId) => resolveNodeLabel(companyId)),
          itemLevel: "level_2",
          children: includeAll
            ? companies.map((companyId) => ({
                title: resolveNodeLabel(companyId),
                level: "level_2",
                items: (companyToSites.get(String(companyId)) || []).map(
                  (site) => resolveNodeLabel(site),
                ),
                itemLevel: "level_3",
              }))
            : [],
        };
      });
    }

    return [];
  }, [
    appliedFilters,
    orgEntityMaps,
    orgNodeById,
    resolveCountryLabel,
    resolveNodeLabel,
    t,
  ]);

  const relationshipTree = useMemo(() => {
    const buildNode = (node) => {
      const children = [];
      if (Array.isArray(node.children) && node.children.length) {
        children.push(...node.children.map((child) => buildNode(child)));
      }
      if (Array.isArray(node.items) && node.items.length) {
        children.push(
          ...node.items.map((item) => ({
            title: item,
            level: node.itemLevel,
            children: [],
          })),
        );
      }
      return {
        title: node.title,
        level: node.level,
        children,
      };
    };

    return relationshipGroups.map((group) => buildNode(group));
  }, [relationshipGroups]);

  const renderRelationshipTree = useCallback(
    (nodes, depth = 0, parentKey = "") =>
      nodes.flatMap((node, index) => {
        const meta = getPreviewUnitMeta(node.level, appliedFilters?.buSide);
        const Icon = meta.icon;
        const hasChildren = node.children?.length > 0;
        const nodeKey = `${parentKey}${parentKey ? ">" : ""}${node.level || "node"}:${node.title}`;
        const isCollapsed = relationshipCollapsedNodes.has(nodeKey);

        return [
          <div
            key={nodeKey || `${node.title}-${node.level || "node"}-${index}`}
          >
            <div
              className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-muted/60"
              style={{ paddingLeft: `${8 + depth * INDENT_SIZE}px` }}
            >
              {hasChildren ? (
                <button
                  type="button"
                  className="flex h-5 w-5 items-center justify-center rounded border border-border bg-background text-muted-foreground"
                  onClick={() => handleToggleRelationshipCollapse(nodeKey)}
                  aria-label={t("Toggle children")}
                >
                  {isCollapsed ? (
                    <ChevronRight className="h-3 w-3" />
                  ) : (
                    <ChevronDown className="h-3 w-3" />
                  )}
                </button>
              ) : (
                <span className="h-5 w-5" />
              )}
              <span className="h-4 w-4" />
              <span
                className={`flex h-6 w-6 items-center justify-center rounded-full bg-background shadow-sm ${meta.color}`}
              >
                <Icon className="h-3 w-3" />
              </span>
              <span className="text-xs text-foreground">{node.title}</span>
            </div>
            {node.children?.length && !isCollapsed
              ? renderRelationshipTree(node.children, depth + 1, nodeKey)
              : null}
          </div>,
        ];
      }),
    [
      appliedFilters?.buSide,
      handleToggleRelationshipCollapse,
      relationshipCollapsedNodes,
      t,
    ],
  );

  const previewTable = previewData?.table || previewData || {};
  const previewRows = useMemo(
    () => (Array.isArray(previewTable?.rows) ? previewTable.rows : []),
    [previewTable?.rows],
  );
  const pillarOptions = useMemo(() => {
    const fromApi = Array.isArray(previewData?.pillarOptions)
      ? previewData.pillarOptions.filter(Boolean)
      : [];
    if (fromApi.length) {
      return fromApi.includes("All") ? fromApi : ["All", ...fromApi];
    }
    const unique = Array.from(
      new Set(previewRows.map((entry) => entry.pillar).filter(Boolean)),
    );
    return unique.length ? ["All", ...unique] : ["All"];
  }, [previewData?.pillarOptions, previewRows]);
  const [pillarFilter, setPillarFilter] = useState("All");
  const [codeSearch, setCodeSearch] = useState("");
  const [energyPage, setEnergyPage] = useState(1);
  const [energyPageSize, setEnergyPageSize] = useState(10);
  const [energySortKey, setEnergySortKey] = useState("code");
  const [energySortDir, setEnergySortDir] = useState("asc");
  const isConsolidated = Boolean(
    previewTable?.consolidated ?? appliedFilters?.consolidation,
  );
  const [yearFilter, setYearFilter] = useState("All");
  const [dimensionFilter, setDimensionFilter] = useState("All");
  // Derived from the actual rows, not re-derived from the filter's own
  // start/end dates — a row's `year` is a fiscal-year label (see
  // buildConsolidatedRows/buildPreviewRows on the API side) which can fall
  // outside a naive calendar-year slice of the filter range (e.g. a fiscal
  // year of Apr-Mar spans two calendar years). Mirrors dimensionOptions
  // below, which already follows this pattern.
  const yearOptions = useMemo(() => {
    const unique = Array.from(
      new Set(previewRows.map((r) => r.year).filter(Boolean)),
    ).sort();
    return ["All", ...unique];
  }, [previewRows]);
  // Display-only text for each year option (e.g. "2019 (Apr 2019 - Mar
  // 2020)") — the filter itself still matches on the bare year value above.
  const yearLabelByYear = useMemo(() => {
    const map = new Map();
    previewRows.forEach((r) => {
      if (r.year && !map.has(r.year)) map.set(r.year, r.yearLabel || r.year);
    });
    return map;
  }, [previewRows]);
  const dimensionConfig = useMemo(() => {
    const buSide = appliedFilters?.buSide;
    if (!buSide || buSide === "parent_company") return { show: false };
    if (isConsolidated) {
      const labelMap = {
        level_1: "BU",
        level_2: "Company",
        level_3: "Site",
        country: "Country",
      };
      return {
        show: true,
        field: "dimensionName",
        label: labelMap[buSide] || "Dimension",
      };
    }
    if (buSide === "level_1") return { show: true, field: "bu", label: "BU" };
    if (buSide === "level_2")
      return { show: true, field: "companyAncestor", label: "Company" };
    if (buSide === "level_3")
      return { show: true, field: "owner", label: "Site" };
    if (buSide === "country")
      return { show: true, field: "country", label: "Country" };
    return { show: false };
  }, [appliedFilters?.buSide, isConsolidated]);
  const dimensionOptions = useMemo(() => {
    if (!dimensionConfig.show) return [];
    const unique = Array.from(
      new Set(previewRows.map((r) => r[dimensionConfig.field]).filter(Boolean)),
    ).sort();
    return ["All", ...unique];
  }, [dimensionConfig, previewRows]);

  const toggleEnergySort = useCallback(
    (key) => {
      if (energySortKey === key) {
        setEnergySortDir((prev) => (prev === "asc" ? "desc" : "asc"));
      } else {
        setEnergySortKey(key);
        setEnergySortDir("asc");
      }
      setEnergyPage(1);
    },
    [energySortKey],
  );

  const normalizedCodeSearch = codeSearch.trim().toUpperCase();
  const filteredEnergyEntries = useMemo(() => {
    const filtered = previewRows.filter((entry) => {
      const matchesPillar =
        pillarFilter === "All" || entry.pillar === pillarFilter;
      const matchesCode =
        !normalizedCodeSearch ||
        String(entry.code || "")
          .toUpperCase()
          .includes(normalizedCodeSearch);
      const matchesYear = yearFilter === "All" || entry.year === yearFilter;
      const matchesDimension =
        !dimensionConfig.show ||
        dimensionFilter === "All" ||
        String(entry[dimensionConfig.field] || "") === dimensionFilter;
      return matchesPillar && matchesCode && matchesYear && matchesDimension;
    });
    return [...filtered].sort((a, b) => {
      const aVal = String(a[energySortKey] ?? "").toLowerCase();
      const bVal = String(b[energySortKey] ?? "").toLowerCase();
      if (aVal < bVal) return energySortDir === "asc" ? -1 : 1;
      if (aVal > bVal) return energySortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [
    normalizedCodeSearch,
    pillarFilter,
    yearFilter,
    dimensionFilter,
    dimensionConfig,
    previewRows,
    energySortKey,
    energySortDir,
  ]);
  const totalEnergyPages = Math.max(
    1,
    Math.ceil(filteredEnergyEntries.length / energyPageSize),
  );
  const pagedEnergyEntries = filteredEnergyEntries.slice(
    (energyPage - 1) * energyPageSize,
    energyPage * energyPageSize,
  );

  useEffect(() => {
    setEnergyPage(1);
  }, [pillarFilter, yearFilter, dimensionFilter, normalizedCodeSearch]);

  useEffect(() => {
    setYearFilter("All");
    setDimensionFilter("All");
    setCodeSearch("");
    setEnergyPage(1);
  }, [previewRows.length]);

  const parseValueUnit = (value, unit) => {
    if (unit) {
      const v =
        value && value.endsWith(` ${unit}`)
          ? value.slice(0, -(unit.length + 1)) || "-"
          : value || "-";
      return [v, unit];
    }
    if (!value) return ["-", "-"];
    const lastSpace = value.lastIndexOf(" ");
    if (lastSpace !== -1) {
      const left = value.slice(0, lastSpace);
      const right = value.slice(lastSpace + 1);
      if (!isNaN(parseFloat(left.replace(/,/g, "")))) return [left, right];
    }
    return [value, "-"];
  };
  const detailGridCols =
    dimensionConfig.show && !isConsolidated
      ? "grid-cols-[0.7fr_0.6fr_1fr_1fr_1.4fr_0.9fr_0.7fr_0.8fr_0.8fr_0.5fr]"
      : "grid-cols-[0.7fr_0.6fr_1.2fr_2fr_0.9fr_0.7fr_0.8fr_0.8fr_0.5fr]";
  const detailColumns = [
    { key: "code", label: t("Code") },
    { key: "crossRefCodes", label: t("Cross-Ref") },
    { key: "codeTitle", label: t("Title") },
    ...(dimensionConfig.show && !isConsolidated
      ? [{ key: dimensionConfig.field, label: t(dimensionConfig.label) }]
      : []),
    { key: "owner", label: t("Organization") },
    { key: "pillar", label: t("ESG Category") },
    { key: "year", label: t("Reporting Year") },
    { key: "date", label: t("Date") },
    { key: "value", label: t("Result") },
    { key: "unit", label: t("Unit") },
  ];
  const consolidatedGridCols = dimensionConfig.show
    ? "grid-cols-[0.7fr_0.6fr_1.2fr_1fr_0.7fr_0.9fr_0.8fr_0.5fr]"
    : "grid-cols-[0.7fr_0.6fr_1.4fr_0.7fr_0.9fr_0.8fr_0.5fr]";
  const consolidatedColumns = [
    { key: "code", label: t("Code") },
    { key: "crossRefCodes", label: t("Cross-Ref") },
    { key: "codeTitle", label: t("Title") },
    ...(dimensionConfig.show
      ? [
          {
            key: "dimensionName",
            label: t(dimensionConfig.label || "Dimension"),
          },
        ]
      : []),
    { key: "year", label: t("Reporting Year") },
    { key: "pillar", label: t("ESG Category") },
    { key: "value", label: t("Result") },
    { key: "unit", label: t("Unit") },
  ];

  return (
    <div className="space-y-6">
      <UnsavedChangesDialog
        open={showUnsavedDialog}
        onConfirm={confirmDiscard}
        onCancel={cancelDiscard}
      />
      <PageHeader
        title={t("ESG Report")}
        icon={FileText}
        description={
          isEditingDraft
            ? t('Editing draft: "{{name}}"', {
                name: editingDraftReport?.name || "",
              })
            : t("Build a ESG report preview before exporting.")
        }
        actions={
          <Button
            variant="outline"
            onClick={() => navigate(PATHS.ESG_REPORT_PREVIEW_LIST)}
          >
            {isEditingDraft ? (
              <>
                <ChevronLeft className="h-4 w-4" />
                {t("Back to Reports")}
              </>
            ) : (
              <>
                <FileText />
                {t("View Created Reports")}
              </>
            )}
          </Button>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[minmax(320px,360px)_1fr] lg:items-stretch">
        <Card className="lg:sticky lg:top-4 lg:z-10">
          <CardHeader className="border-b">
            <div className="space-y-1">
              <CardTitle className="flex items-center gap-2">
                <Filter className="h-4 w-4" />
                {t("Filters")}
              </CardTitle>
              <CardDescription>
                {isEditingDraft
                  ? t(
                      "Edit the draft filters, apply changes, then save the draft.",
                    )
                  : t("Select filters, then apply to generate a preview.")}
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            <>
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm">{t("Consolidation")}</Label>
                  <label className="flex items-center gap-2 text-sm text-foreground">
                    <Checkbox
                      checked={selectedFilters.consolidation}
                      onCheckedChange={(value) =>
                        setFilter("consolidation", Boolean(value))
                      }
                    />
                    {t("Enable consolidation")}
                  </label>
                  {selectedFilters.consolidation ? (
                    <p className="text-xs text-muted-foreground">
                      {t("Consolidated below filters.")}
                    </p>
                  ) : null}
                </div>

                <div className="grid gap-4">
                  <PickerField
                    label={t("Reporting Year")}
                    error={
                      showValidation && !selectedFilters.startDate
                        ? t("Reporting year is required")
                        : null
                    }
                    className="space-y-2"
                  >
                    <SelectField
                      options={reportingYearOptions}
                      sortOptions={false}
                      value={selectedReportingBoundaryId}
                      onValueChange={(boundaryId) => {
                        const boundary = reportingYearOptions.find(
                          (option) => option.id === boundaryId,
                        );
                        const boundaryDoc = (
                          boundaryData?.boundaries || []
                        ).find((b) => b.id === boundaryId);
                        if (!boundary || !boundaryDoc) return;
                        isHydratingRef.current = false;
                        setFilter(
                          "startDate",
                          toYearMonthValue({
                            year: Number(boundaryDoc.startYear),
                            month: Number(boundaryDoc.startMonth),
                          }),
                        );
                        setFilter(
                          "endDate",
                          toYearMonthValue({
                            year: Number(boundaryDoc.endYear),
                            month: Number(boundaryDoc.endMonth),
                          }),
                        );
                      }}
                      placeholder={t("Select a reporting year")}
                    />
                  </PickerField>
                </div>

                <div className="space-y-2">
                  <Label className="text-sm">{t("Filter")}</Label>
                  <SelectField
                    key={`preview-filter-${selectedFilters.buSide || "empty"}`}
                    options={REPORT_PREVIEW_OPTIONS.buSides.filter(
                      (o) => o.value !== "parent_company" || showParentOption,
                    )}
                    sortOptions={false}
                    value={selectedFilters.buSide}
                    onValueChange={(value) => {
                      isHydratingRef.current = false;
                      setFilter("buSide", value);
                    }}
                    placeholder={t("Select an option")}
                    disabled={!datesSelected}
                  />
                  {!datesSelected ? (
                    <p className="text-xs text-muted-foreground">
                      {t("Select a reporting year first.")}
                    </p>
                  ) : null}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <Label className="text-sm">{t("Name")}</Label>
                    {allowIncludeAllEntities ? (
                      <label className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Checkbox
                          checked={Boolean(selectedFilters.includeAllEntities)}
                          onCheckedChange={(value) =>
                            setFilter("includeAllEntities", Boolean(value))
                          }
                        />
                        {t("Include all entities data")}
                      </label>
                    ) : null}
                  </div>
                  <Popover
                    open={nameDropdownOpen}
                    onOpenChange={setNameDropdownOpen}
                    modal
                  >
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        disabled={!datesSelected || !selectedFilters.buSide}
                        className={cn(
                          "w-full justify-between border border-border bg-card min-h-10 h-auto items-start py-2",
                          showValidation &&
                            selectedFilters.companies.length === 0 &&
                            "border-destructive",
                        )}
                      >
                        {selectedFilters.companies.length === 0 ? (
                          <span className="truncate text-sm text-foreground">
                            {t("Select organizations")}
                          </span>
                        ) : (
                          <div className="flex flex-1 flex-wrap items-center gap-1 min-w-0">
                            {selectedNameLabels.slice(0, 3).map((value) => (
                              <Badge
                                key={value}
                                variant="outline"
                                className="bg-background text-xs max-w-full"
                              >
                                {value}
                              </Badge>
                            ))}
                            {selectedFilters.companies.length > 3 ? (
                              <Badge
                                variant="outline"
                                className="bg-background text-xs"
                              >
                                +{selectedFilters.companies.length - 3}
                              </Badge>
                            ) : null}
                          </div>
                        )}
                        <ChevronsUpDown className="h-4 w-4 text-muted-foreground self-center" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-[min(28rem,calc(100vw-1rem))] min-w-[--radix-popover-trigger-width] max-w-[calc(100vw-1rem)] p-0"
                      align="start"
                    >
                      <div className="border-b border-border p-3">
                        <Input
                          value={nameQuery}
                          onChange={(event) => setNameQuery(event.target.value)}
                          placeholder={t("Search organizations...")}
                        />
                      </div>
                      <div className="max-h-[320px] overflow-y-auto p-2">
                        {orgLoading ? (
                          <div className="p-3 text-sm text-muted-foreground">
                            {t("Loading organizations...")}
                          </div>
                        ) : visibleTree.length ? (
                          visibleTree.map((node) => (
                            <NameTreeNode
                              key={getNodeKey(node) || node.name}
                              node={node}
                              depth={0}
                              selectedSet={selectedNameSet}
                              collapsedNodes={collapsedNodes}
                              onToggleCollapse={handleToggleCollapse}
                              onToggleSelect={handleToggleName}
                              onBatchToggle={handleBatchToggle}
                              selectableLevels={selectableLevels}
                              filterType={selectedFilters.buSide}
                              statusMap={orgStatusMap}
                            />
                          ))
                        ) : (
                          <div className="p-3 text-sm text-muted-foreground">
                            {t("No organizations match your search.")}
                          </div>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                  {showValidation && selectedFilters.companies.length === 0 ? (
                    <p className="text-sm text-destructive">
                      {t("Select at least one company")}
                    </p>
                  ) : null}
                </div>

                {frameworkOptions.length > 0 ? (
                  <div className="space-y-2">
                    <Label className="text-sm">{t("Frameworks")}</Label>
                    <MultiSelect
                      options={frameworkOptions.map((code) => ({
                        value: code,
                        label: assignedFrameworkNameByCode.get(code) || code,
                      }))}
                      value={
                        Array.isArray(selectedFilters.frameworks)
                          ? selectedFilters.frameworks
                          : []
                      }
                      onChange={(values) => setFilter("frameworks", values)}
                      placeholder={t("All frameworks")}
                      disabled={!datesSelected}
                      maxDisplay={3}
                    />
                    {!selectedFilters.frameworks?.length ? (
                      <p className="text-xs text-muted-foreground">
                        {t("All frameworks included — select to filter.")}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 rounded-radius border border-dashed border-border bg-muted/20 px-3 py-2">
                <div className="text-xs text-muted-foreground">
                  {showValidation && requiredMissing
                    ? t("Select required filters before applying.")
                    : t("Filters only apply when you confirm.")}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={handleReset}>
                    {t("Reset")}
                  </Button>
                  <Button disabled={requiredMissing} onClick={handleApply}>
                    {t("Apply Filters")}
                  </Button>
                </div>
              </div>
            </>
          </CardContent>
        </Card>

        <Card className="flex h-full min-h-[320px] flex-col">
          <CardHeader className="border-b">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="space-y-1">
                <CardTitle>{t("Preview")}</CardTitle>
                <CardDescription>
                  {t("Preview output based on applied filters.")}
                </CardDescription>
              </div>
              {isEditingDraft ? (
                <div className="flex items-center">
                  <Can
                    resource="esg_reporting"
                    action="update"
                    userPermissions={permissions}
                  >
                    <Button
                      disabled={
                        !canCreate || filteredEnergyEntries.length === 0
                      }
                      className="rounded-r-none"
                      onClick={() =>
                        openCreateModal("edit-draft", editingDraftReport)
                      }
                    >
                      {t("Update Changes")}
                    </Button>
                  </Can>
                  <Can
                    resource="esg_reporting"
                    action="update"
                    userPermissions={permissions}
                  >
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          disabled={
                            !canCreate || filteredEnergyEntries.length === 0
                          }
                          className="rounded-l-none border-l border-l-primary-foreground/20 px-2"
                          aria-label={t("More actions")}
                        >
                          <ChevronDown className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52">
                        <DropdownMenuItem
                          onClick={() => setIsCompleteConfirmOpen(true)}
                        >
                          <CheckCheck className="h-4 w-4" />
                          {t("Mark as Completed")}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </Can>
                </div>
              ) : (
                <Can
                  resource="esg_reporting"
                  action="create"
                  userPermissions={permissions}
                >
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        disabled={
                          !canCreate || filteredEnergyEntries.length === 0
                        }
                      >
                        {t("Create Report")}
                        <ChevronDown className="ml-1 h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuItem
                        onClick={() => openCreateModal("draft")}
                      >
                        {t("Save Draft")}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => openCreateModal("completed")}
                      >
                        {t("Create Completed Report")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </Can>
              )}
            </div>
          </CardHeader>
          <CardContent className="flex flex-1 flex-col space-y-4">
            {!appliedFilters && !previewLoading ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-3 rounded-radius border border-dashed border-border bg-muted/20 p-10 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-radius bg-primary/10 text-primary">
                  <FileText className="h-6 w-6" />
                </span>
                <div className="space-y-1">
                  <p className="text-base font-semibold text-foreground">
                    {t("Preview not generated yet")}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {showValidation && requiredMissing
                      ? t("Required filters are missing. Apply once complete.")
                      : t("Apply filters to generate a preview.")}
                  </p>
                  {previewError ? (
                    <p className="text-sm text-destructive">{previewError}</p>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {appliedFilters && (
                  <div className="rounded-radius border border-border bg-muted/20 px-3 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="flex shrink-0 items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/80">
                        {t("Organizations")}
                        {relationshipGroups.length ? (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-border bg-background text-muted-foreground transition hover:bg-muted/60 hover:text-foreground"
                                aria-label={t("View relationship")}
                              >
                                <Info className="h-3.5 w-3.5" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-80 p-3" align="start">
                              <div className="text-xs font-semibold text-foreground">
                                {appliedFilters?.buSide === "country"
                                  ? t("Country")
                                  : appliedFilters?.buSide === "parent_company"
                                    ? t("Parent")
                                    : t("Organization")}
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {appliedFilters?.buSide === "country"
                                  ? t("Selected countries only.")
                                  : appliedFilters?.buSide === "parent_company"
                                    ? t("Selected parent company scope.")
                                    : t("Selected organization only.")}
                              </p>
                              <div className="mt-2 max-h-56 space-y-1 overflow-y-auto pr-1">
                                {appliedFilters?.buSide === "country" ? (
                                  <div className="flex flex-col gap-1 text-xs text-muted-foreground">
                                    {appliedCountryLabels.length
                                      ? appliedCountryLabels.map((country) => (
                                          <span
                                            key={country}
                                            className="flex items-center gap-2 truncate"
                                          >
                                            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-background shadow-sm text-emerald-600">
                                              <Globe2 className="h-3 w-3" />
                                            </span>
                                            {country}
                                          </span>
                                        ))
                                      : t("No countries selected.")}
                                  </div>
                                ) : relationshipTree.length ? (
                                  renderRelationshipTree(relationshipTree)
                                ) : (
                                  <p className="text-xs text-muted-foreground">
                                    {t("No relationship data available.")}
                                  </p>
                                )}
                              </div>
                            </PopoverContent>
                          </Popover>
                        ) : null}
                      </div>
                      <div className="ml-auto flex flex-wrap items-center gap-2">
                        <div className="relative">
                          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                          <Input
                            value={codeSearch}
                            onChange={(event) =>
                              setCodeSearch(event.target.value)
                            }
                            placeholder={t("Search by code")}
                            className="h-8 w-44 bg-card pl-7 text-xs"
                          />
                        </div>
                        <Select
                          value={yearFilter}
                          onValueChange={setYearFilter}
                        >
                          <SelectTrigger className="h-8 min-w-44 max-w-72 text-xs">
                            <SelectValue placeholder={t("Reporting Year")} />
                          </SelectTrigger>
                          <SelectContent>
                            {yearOptions.map((y) => (
                              <SelectItem key={y} value={y}>
                                {y === "All"
                                  ? t("All Reporting Years")
                                  : yearLabelByYear.get(y) || y}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {dimensionConfig.show && (
                          <Select
                            value={dimensionFilter}
                            onValueChange={setDimensionFilter}
                          >
                            <SelectTrigger className="h-8 min-w-28 max-w-56 text-xs">
                              <SelectValue
                                placeholder={t(dimensionConfig.label)}
                              />
                            </SelectTrigger>
                            <SelectContent>
                              {dimensionOptions.map((opt) => (
                                <SelectItem key={opt} value={opt}>
                                  {opt === "All"
                                    ? t("All {{label}}s", {
                                        label: t(dimensionConfig.label),
                                      })
                                    : opt}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        <Select
                          value={pillarFilter}
                          onValueChange={setPillarFilter}
                        >
                          <SelectTrigger className="h-8 w-36 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {pillarOptions.map((pillar) => (
                              <SelectItem key={pillar} value={pillar}>
                                {pillar === "All" ? t("All") : pillar}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <div key={isConsolidated ? "consolidated" : "detail"}>
                    {isConsolidated ? (
                      <div className="h-[420px] overflow-y-auto rounded-radius border border-border bg-card">
                        <div
                          className={`sticky top-0 z-10 grid ${consolidatedGridCols} gap-2 border-b border-border bg-muted px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground`}
                        >
                          {consolidatedColumns.map(({ key, label }) => (
                            <button
                              key={key}
                              type="button"
                              className="flex items-center gap-1 text-left hover:text-foreground"
                              onClick={() => toggleEnergySort(key)}
                            >
                              {label}
                              {energySortKey === key ? (
                                energySortDir === "asc" ? (
                                  <ChevronUp className="h-3 w-3" />
                                ) : (
                                  <ChevronDown className="h-3 w-3" />
                                )
                              ) : (
                                <ChevronsUpDown className="h-3 w-3 opacity-40" />
                              )}
                            </button>
                          ))}
                        </div>
                        <div className="divide-y divide-border bg-card text-xs text-foreground">
                          {previewLoading ? (
                            Array.from({ length: energyPageSize }).map(
                              (_, i) => (
                                <div
                                  key={`skeleton-${i}`}
                                  className={`grid ${consolidatedGridCols} gap-2 px-3 py-2`}
                                >
                                  {consolidatedColumns.map(({ key }) => (
                                    <Skeleton key={key} className="h-4" />
                                  ))}
                                </div>
                              ),
                            )
                          ) : pagedEnergyEntries.length === 0 ? (
                            <div className="flex h-[370px] flex-col items-center justify-center gap-2 text-center">
                              <span className="flex h-12 w-12 items-center justify-center rounded-radius bg-primary/10 text-primary">
                                <SearchX className="h-6 w-6" />
                              </span>
                              <p className="text-base font-semibold text-foreground">
                                {t("No data found")}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {t(
                                  "Try adjusting your filters and search again.",
                                )}
                              </p>
                            </div>
                          ) : (
                            pagedEnergyEntries.map((item) => (
                              <div
                                key={`${item.code}-${item.dimensionName}-${item.year}-${item.value}`}
                                className={`grid ${consolidatedGridCols} gap-2 px-3 py-2`}
                              >
                                <span>{item.code}</span>
                                <span className="text-muted-foreground">
                                  {Array.isArray(item.crossRefCodes) &&
                                  item.crossRefCodes.length > 0
                                    ? item.crossRefCodes.join(", ")
                                    : "-"}
                                </span>
                                <span>{item.codeTitle || "-"}</span>
                                {dimensionConfig.show && (
                                  <span>{item.dimensionName || "-"}</span>
                                )}
                                <span>
                                  {item.yearLabel || item.year || "-"}
                                </span>
                                <span>{item.pillar}</span>
                                {(() => {
                                  const [vd, ud] =
                                    !item.value && item.remark
                                      ? ["n.a", "-"]
                                      : parseValueUnit(item.value, item.unit);
                                  return (
                                    <>
                                      <span>{vd}</span>
                                      <span className="text-muted-foreground">
                                        {ud}
                                      </span>
                                    </>
                                  );
                                })()}
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="h-[420px] overflow-y-auto rounded-radius border border-border bg-card">
                        <div
                          className={`sticky top-0 z-10 grid ${detailGridCols} gap-2 border-b border-border bg-muted px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground`}
                        >
                          {detailColumns.map(({ key, label }) => (
                            <button
                              key={key}
                              type="button"
                              className="flex items-center gap-1 text-left hover:text-foreground"
                              onClick={() => toggleEnergySort(key)}
                            >
                              {label}
                              {energySortKey === key ? (
                                energySortDir === "asc" ? (
                                  <ChevronUp className="h-3 w-3" />
                                ) : (
                                  <ChevronDown className="h-3 w-3" />
                                )
                              ) : (
                                <ChevronsUpDown className="h-3 w-3 opacity-40" />
                              )}
                            </button>
                          ))}
                        </div>
                        <div className="divide-y divide-border bg-card text-xs text-foreground">
                          {previewLoading ? (
                            Array.from({ length: energyPageSize }).map(
                              (_, i) => (
                                <div
                                  key={`skeleton-${i}`}
                                  className={`grid ${detailGridCols} gap-2 px-3 py-2`}
                                >
                                  {detailColumns.map(({ key }) => (
                                    <Skeleton key={key} className="h-4" />
                                  ))}
                                </div>
                              ),
                            )
                          ) : pagedEnergyEntries.length === 0 ? (
                            <div className="flex h-[370px] flex-col items-center justify-center gap-2 text-center">
                              <span className="flex h-12 w-12 items-center justify-center rounded-radius bg-primary/10 text-primary">
                                <SearchX className="h-6 w-6" />
                              </span>
                              <p className="text-base font-semibold text-foreground">
                                {t("No data found")}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {t(
                                  "Try adjusting your filters and search again.",
                                )}
                              </p>
                            </div>
                          ) : (
                            pagedEnergyEntries.map((item) => {
                              const hierarchyNames =
                                Array.isArray(item.ancestorPath) &&
                                item.ancestorPath.length
                                  ? item.ancestorPath
                                  : [item.parentLevel, item.owner].filter(
                                      Boolean,
                                    );
                              const hierarchyTrail = hierarchyNames.map(
                                (name, i) => {
                                  const info = orgNameToStatusMap.get(name);
                                  return {
                                    id: i,
                                    name,
                                    status: info?.status,
                                    inactiveDate: info?.inactiveDate,
                                  };
                                },
                              );
                              return (
                                <div
                                  key={`${item.code}-${item.owner}-${item.year}-${item.date}-${item.value}`}
                                  className={`grid ${detailGridCols} gap-2 px-3 py-2`}
                                >
                                  <span>{item.code}</span>
                                  <span className="text-muted-foreground">
                                    {Array.isArray(item.crossRefCodes) &&
                                    item.crossRefCodes.length > 0
                                      ? item.crossRefCodes.join(", ")
                                      : "-"}
                                  </span>
                                  <span>{item.codeTitle || "-"}</span>
                                  {dimensionConfig.show && (
                                    <span>
                                      {item[dimensionConfig.field] || "-"}
                                    </span>
                                  )}
                                  <Breadcrumbs
                                    trail={hierarchyTrail}
                                    activeColor="text-foreground font-medium"
                                    compact
                                  />
                                  <span>{item.pillar}</span>
                                  <span>
                                    {item.yearLabel || item.year || "-"}
                                  </span>
                                  <span>{item.date}</span>
                                  {(() => {
                                    const [vd, ud] =
                                      !item.value && item.remark
                                        ? ["n.a", "-"]
                                        : parseValueUnit(item.value, item.unit);
                                    return (
                                      <>
                                        <span>{vd}</span>
                                        <span className="text-muted-foreground">
                                          {ud}
                                        </span>
                                      </>
                                    );
                                  })()}
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between border-t border-border py-3">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>{t("Rows per page")}</span>
                      <Select
                        value={String(energyPageSize)}
                        onValueChange={(v) => {
                          setEnergyPageSize(Number(v));
                          setEnergyPage(1);
                        }}
                      >
                        <SelectTrigger size="sm" className="w-16">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[10, 20, 50].map((n) => (
                            <SelectItem key={n} value={String(n)}>
                              {n}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <span>
                        {filteredEnergyEntries.length === 0
                          ? t("0 results")
                          : t("{{from}}–{{to}} of {{total}}", {
                              from: (energyPage - 1) * energyPageSize + 1,
                              to: Math.min(
                                energyPage * energyPageSize,
                                filteredEnergyEntries.length,
                              ),
                              total: filteredEnergyEntries.length,
                            })}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={energyPage === 1}
                        onClick={() => setEnergyPage(1)}
                        aria-label={t("First page")}
                      >
                        «
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={energyPage === 1}
                        onClick={() => setEnergyPage((p) => p - 1)}
                        aria-label={t("Previous page")}
                      >
                        ‹
                      </Button>
                      <span className="min-w-[90px] text-center text-sm text-muted-foreground">
                        {t("Page {{page}} of {{total}}", {
                          page: energyPage,
                          total: totalEnergyPages,
                        })}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={energyPage === totalEnergyPages}
                        onClick={() => setEnergyPage((p) => p + 1)}
                        aria-label={t("Next page")}
                      >
                        ›
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={energyPage === totalEnergyPages}
                        onClick={() => setEnergyPage(totalEnergyPages)}
                        aria-label={t("Last page")}
                      >
                        »
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <AppDialog
        open={isCompleteConfirmOpen}
        onOpenChange={setIsCompleteConfirmOpen}
        title={t("Mark as Completed")}
        description={t(
          "Completed reports are stored as fixed snapshots and can no longer be edited.",
        )}
        size="sm"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setIsCompleteConfirmOpen(false)}
            >
              {t("Cancel")}
            </Button>
            <Can
              resource="esg_reporting"
              action="update"
              userPermissions={permissions}
            >
              <Button onClick={handleMarkAsCompleted}>
                {t("Mark as Completed")}
              </Button>
            </Can>
          </>
        }
      >
        <p className="text-sm text-muted-foreground">
          {t('Save current changes and mark "{{name}}" as completed?', {
            name: editingDraftReport?.name || "",
          })}
        </p>
      </AppDialog>

      <CreateReportModal bypassUnsavedChanges={bypass} />
    </div>
  );
});

const PreviewReportPage = () => (
  <ReportPreviewProvider>
    <PreviewReportPageContent />
  </ReportPreviewProvider>
);

export default PreviewReportPage;
